import React, { useEffect, useMemo, useRef, useState } from 'react';
import { rsvpReconcileAPI } from '../lib/api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from './ui/card';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from './ui/command';
import {
  ChevronDown,
  ChevronUp,
  ClipboardCheck,
  Loader2,
  UserPlus,
  Copy,
  X,
  FileImage,
  Sparkles,
  Pencil,
  Check,
  Trash2,
} from 'lucide-react';

/**
 * RSVP Reconcile Panel — collapsible tool for cross-checking a WhatsApp RSVP
 * list against registered attendees for a given event.
 *
 * Every row can be manually overridden via a searchable picker that shows the
 * top-3 fuzzy suggestions AND the full attendee list (perfect for nicknames
 * like "Nash" -> "Naushad Ahmed"). Duplicates (same registered person on the
 * list twice) are flagged with a DUP badge so the tally stays honest. The
 * entire reconciliation persists per-event server-side until you Clear it.
 */
export default function RsvpReconcilePanel({ eventId, attendees, onWalkInAdded }) {
  const [open, setOpen] = useState(false);
  const [namesText, setNamesText] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loadingState, setLoadingState] = useState(true);
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const [walkInName, setWalkInName] = useState('');
  const [walkInEmail, setWalkInEmail] = useState('');
  const [walkInOpen, setWalkInOpen] = useState(false);
  const [walkInSaving, setWalkInSaving] = useState(false);
  const [walkInRowIdx, setWalkInRowIdx] = useState(null);
  const fileInputRef = useRef(null);

  // ---------- Persistence: load saved state on mount / event change ----------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingState(true);
      try {
        const res = await rsvpReconcileAPI.getState(eventId);
        if (cancelled) return;
        if (res.data && res.data.rows?.length) {
          setRows(res.data.rows);
          setSummary(res.data.summary);
          setOpen(true);   // auto-expand if there's a saved list to review
        }
      } catch (e) {
        // silent — no state yet is fine
      } finally {
        if (!cancelled) setLoadingState(false);
      }
    })();
    return () => { cancelled = true; };
  }, [eventId]);

  // ---------- Helpers ----------
  const _fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const handleImageChoose = (file) => {
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp)$/i.test(file.type)) {
      toast.error('Please choose a PNG, JPEG, or WebP image');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Image too large (max 8 MB)');
      return;
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const clearImage = () => {
    setImageFile(null);
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Save current rows back to the server; server recomputes duplicates & summary
  const persistRows = async (nextRows) => {
    try {
      const res = await rsvpReconcileAPI.saveState(eventId, nextRows);
      if (res.data?.rows) setRows(res.data.rows);
      if (res.data?.summary) setSummary(res.data.summary);
    } catch (e) {
      toast.error('Failed to save reconciliation');
    }
  };

  const handleReconcile = async () => {
    if (!namesText.trim() && !imageFile) {
      toast.error('Paste some names or upload a screenshot first');
      return;
    }
    setBusy(true);
    try {
      const payload = { names_text: namesText };
      if (imageFile) payload.image_base64 = await _fileToBase64(imageFile);
      const res = await rsvpReconcileAPI.reconcile(eventId, payload);
      setRows(res.data.rows || []);
      setSummary(res.data.summary || null);
      setNamesText('');
      clearImage();
      toast.success(
        `Reconciled ${res.data.summary?.total || 0} names — ` +
        `${res.data.summary?.R || 0} matched · ` +
        `${res.data.summary?.NS || 0} not sure · ` +
        `${res.data.summary?.NR || 0} not registered`,
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reconciliation failed');
    } finally {
      setBusy(false);
    }
  };

  const handleClearAll = async () => {
    try {
      await rsvpReconcileAPI.clearState(eventId);
      setRows([]);
      setSummary(null);
      setClearConfirmOpen(false);
      toast.success('Reconciliation cleared');
    } catch (e) {
      toast.error('Failed to clear');
    }
  };

  // Manually set a row's match to a specific attendee (works from any status)
  const handlePickMatch = (rowIndex, attendee) => {
    if (!attendee) return;
    const nextRows = rows.map((r, i) => (
      i === rowIndex
        ? {
            ...r,
            status: 'R',
            matched_attendee_id: attendee.id,
            matched_name: attendee.name,
            best_score: 100,
            manually_set: true,
          }
        : r
    ));
    persistRows(nextRows);
    toast.success(`Matched to ${attendee.name}`);
  };

  const handleForceNR = (rowIndex) => {
    const nextRows = rows.map((r, i) => (
      i === rowIndex
        ? {
            ...r,
            status: 'NR',
            matched_attendee_id: null,
            matched_name: null,
            manually_set: true,
          }
        : r
    ));
    persistRows(nextRows);
  };

  const handleRemoveRow = (rowIndex) => {
    const nextRows = rows.filter((_, i) => i !== rowIndex);
    persistRows(nextRows);
  };

  const openWalkIn = (rowIdx, row) => {
    setWalkInRowIdx(rowIdx);
    setWalkInName(row.input_name);
    setWalkInEmail('');
    setWalkInOpen(true);
  };

  const handleSaveWalkIn = async () => {
    if (!walkInName.trim()) {
      toast.error('Name is required');
      return;
    }
    setWalkInSaving(true);
    try {
      const res = await rsvpReconcileAPI.addWalkin(eventId, {
        name: walkInName.trim(),
        email: walkInEmail.trim(),
      });
      const newAttendee = res.data;
      const nextRows = rows.map((r, i) => (
        i === walkInRowIdx
          ? {
              ...r,
              status: 'R',
              matched_attendee_id: newAttendee.id,
              matched_name: newAttendee.name,
              best_score: 100,
              manually_set: true,
            }
          : r
      ));
      await persistRows(nextRows);
      toast.success(`${walkInName} added as walk-in`);
      setWalkInOpen(false);
      if (onWalkInAdded) onWalkInAdded();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add walk-in');
    } finally {
      setWalkInSaving(false);
    }
  };

  // ---------- Display helpers ----------
  const attendeesById = useMemo(() => {
    const m = {};
    (attendees || []).forEach((a) => { m[a.id] = a; });
    return m;
  }, [attendees]);

  const copySummary = () => {
    if (!rows.length) return;
    // WhatsApp-friendly numbered list. When we have a confirmed match, use
    // the REGISTERED full name (correct spelling) rather than the WhatsApp
    // typo. NR rows keep the original name as typed.
    const lines = rows.map((r, i) => {
      const displayName =
        r.status === 'R' && r.matched_name
          ? r.matched_name
          : r.input_name;
      let tag;
      if (r.status === 'R') tag = '(R)';
      else if (r.status === 'NS') tag = '(?)';
      else tag = '(NR)';
      return `${i + 1}. ${displayName} ${tag}`;
    });
    const totals = summary
      ? `\n\n${summary.unique_matched} registered · ${summary.NR} not registered` +
        (summary.NS ? ` · ${summary.NS} to review` : '') +
        (summary.duplicates ? ` · ${summary.duplicates} duplicate` : '')
      : '';
    navigator.clipboard.writeText(lines.join('\n') + totals);
    toast.success('Copied — ready to paste into WhatsApp');
  };

  const statusBadge = (row) => {
    if (row.status === 'R') {
      return row.is_duplicate ? (
        <Badge className="bg-amber-100 text-amber-900 hover:bg-amber-100 border-amber-300" data-testid="status-dup">
          DUP
        </Badge>
      ) : (
        <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 border-emerald-200" data-testid="status-r">
          R
        </Badge>
      );
    }
    if (row.status === 'NS') return <Badge className="bg-yellow-100 text-yellow-900 hover:bg-yellow-100 border-yellow-300" data-testid="status-ns">NS</Badge>;
    return <Badge className="bg-rose-100 text-rose-800 hover:bg-rose-100 border-rose-200" data-testid="status-nr">NR</Badge>;
  };

  return (
    <Card className="border-border" data-testid="rsvp-reconcile-panel">
      <CardHeader
        className="flex flex-row items-center justify-between gap-4 cursor-pointer"
        onClick={() => setOpen((v) => !v)}
        data-testid="rsvp-reconcile-toggle"
      >
        <div className="flex-1">
          <CardTitle className="text-base flex items-center gap-2 flex-wrap">
            <ClipboardCheck className="w-4 h-4 text-primary" />
            Reconcile WhatsApp RSVP List
            <Badge variant="secondary" className="ml-1 gap-1 text-[10px]">
              <Sparkles className="w-3 h-3" />
              AI
            </Badge>
            {rows.length > 0 && (
              <Badge variant="outline" className="ml-1 text-[10px]" data-testid="rsvp-saved-badge">
                {summary?.unique_matched ?? 0} matched / {summary?.total ?? 0} saved
              </Badge>
            )}
          </CardTitle>
          <CardDescription className="text-xs mt-1">
            Paste names or upload a screenshot to see who&apos;s registered, who&apos;s unsure, and who&apos;s missing. Pick manually from the full list for nicknames.
          </CardDescription>
        </div>
        <Button variant="ghost" size="icon" className="shrink-0" aria-label={open ? 'Collapse' : 'Expand'}>
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </Button>
      </CardHeader>

      {open && (
        <CardContent className="pt-0 space-y-4">
          {loadingState && (
            <div className="text-xs text-muted-foreground flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin" /> Loading saved list…
            </div>
          )}

          {/* Input area */}
          <div className="grid gap-3 md:grid-cols-[1fr_auto_1fr]">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-muted-foreground">Paste names</Label>
              <Textarea
                placeholder={'1. Dr Priya Sharma\n2. Nash\n3. J. Rao\n…one name per line'}
                value={namesText}
                onChange={(e) => setNamesText(e.target.value)}
                className="min-h-[140px] font-mono text-xs"
                data-testid="rsvp-names-textarea"
              />
            </div>

            <div className="hidden md:flex flex-col items-center justify-center text-xs text-muted-foreground">
              <div className="h-full w-px bg-border" />
              <span className="my-2 px-2 py-1 bg-background border border-border rounded-full">OR</span>
              <div className="h-full w-px bg-border" />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-muted-foreground">Upload WhatsApp screenshot</Label>
              {imagePreview ? (
                <div className="relative border border-border rounded-lg overflow-hidden">
                  <img src={imagePreview} alt="RSVP preview" className="w-full h-[140px] object-contain bg-muted/30" />
                  <Button size="icon" variant="secondary" onClick={clearImage} className="absolute top-2 right-2 h-6 w-6" data-testid="rsvp-image-clear">
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ) : (
                <label
                  htmlFor="rsvp-image-input"
                  className="flex flex-col items-center justify-center h-[140px] rounded-lg border-2 border-dashed border-border bg-muted/20 hover:bg-muted/40 cursor-pointer transition-colors text-muted-foreground"
                  data-testid="rsvp-image-dropzone"
                >
                  <FileImage className="w-6 h-6 mb-1.5" />
                  <span className="text-xs">Drop / choose a screenshot</span>
                  <span className="text-[10px] mt-0.5">PNG · JPEG · WebP · max 8MB</span>
                </label>
              )}
              <input
                id="rsvp-image-input"
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={(e) => handleImageChoose(e.target.files?.[0])}
                className="hidden"
                data-testid="rsvp-image-input"
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              onClick={handleReconcile}
              disabled={busy || (!namesText.trim() && !imageFile)}
              className="gap-2"
              data-testid="rsvp-reconcile-run"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {busy ? 'Reconciling…' : rows.length ? 'Reconcile more' : 'Reconcile'}
            </Button>
            {rows.length > 0 && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copySummary}
                  className="gap-1.5"
                  data-testid="rsvp-copy-summary"
                >
                  <Copy className="w-3.5 h-3.5" />
                  Copy for WhatsApp
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setClearConfirmOpen(true)}
                  className="gap-1.5 text-muted-foreground hover:text-destructive"
                  data-testid="rsvp-clear-all"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Clear
                </Button>
                <div className="ml-auto flex items-center gap-1.5 text-xs text-muted-foreground flex-wrap" data-testid="rsvp-summary-strip">
                  <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">
                    {summary?.unique_matched ?? 0} matched
                  </Badge>
                  <Badge className="bg-yellow-100 text-yellow-900 border-yellow-300">
                    {summary?.NS ?? 0} not sure
                  </Badge>
                  <Badge className="bg-rose-100 text-rose-800 border-rose-200">
                    {summary?.NR ?? 0} missing
                  </Badge>
                  {summary?.duplicates > 0 && (
                    <Badge className="bg-amber-100 text-amber-900 border-amber-300">
                      {summary.duplicates} duplicate
                    </Badge>
                  )}
                  <span>of {summary?.total ?? 0}</span>
                </div>
              </>
            )}
          </div>

          {/* Result rows */}
          {rows.length > 0 && (
            <div className="border border-border rounded-lg divide-y divide-border overflow-hidden">
              {rows.map((row, idx) => (
                <ReconcileRow
                  key={`${row.input_name}-${idx}`}
                  idx={idx}
                  row={row}
                  attendees={attendees || []}
                  attendeesById={attendeesById}
                  statusBadge={statusBadge}
                  onPickMatch={handlePickMatch}
                  onForceNR={handleForceNR}
                  onRemove={handleRemoveRow}
                  onWalkIn={openWalkIn}
                />
              ))}
            </div>
          )}
        </CardContent>
      )}

      {/* Walk-in dialog */}
      <Dialog open={walkInOpen} onOpenChange={setWalkInOpen}>
        <DialogContent data-testid="rsvp-walkin-dialog">
          <DialogHeader>
            <DialogTitle>Add walk-in attendee</DialogTitle>
            <DialogDescription>
              Creates a new registration on this event. Email is optional — leave it blank if you don&apos;t have one.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="walkin-name">Name</Label>
              <Input id="walkin-name" value={walkInName} onChange={(e) => setWalkInName(e.target.value)} data-testid="rsvp-walkin-name" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="walkin-email" className="flex items-center gap-2">
                Email <span className="text-xs text-muted-foreground">(optional)</span>
              </Label>
              <Input id="walkin-email" type="email" placeholder="e.g. person@example.com" value={walkInEmail} onChange={(e) => setWalkInEmail(e.target.value)} data-testid="rsvp-walkin-email" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWalkInOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveWalkIn} disabled={walkInSaving || !walkInName.trim()} className="gap-2" data-testid="rsvp-walkin-save">
              {walkInSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
              Add walk-in
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clear-all confirmation */}
      <AlertDialog open={clearConfirmOpen} onOpenChange={setClearConfirmOpen}>
        <AlertDialogContent data-testid="rsvp-clear-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Clear this reconciliation?</AlertDialogTitle>
            <AlertDialogDescription>
              This wipes the saved WhatsApp RSVP reconciliation for this event so you can start a new one. Registered attendees are not affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleClearAll} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="rsvp-clear-confirm-btn">
              Clear
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

/**
 * A single row in the reconciliation table with an always-available "Change
 * match" popover that shows top-3 fuzzy suggestions plus the FULL attendee
 * list (searchable).
 */
function ReconcileRow({
  idx,
  row,
  attendees,
  attendeesById,
  statusBadge,
  onPickMatch,
  onForceNR,
  onRemove,
  onWalkIn,
}) {
  const [pickerOpen, setPickerOpen] = useState(false);

  // Build the picker items: top-3 fuzzy suggestions (already on the row),
  // then the full attendee list (excluding those already suggested).
  const suggestionIds = new Set((row.candidates || []).map((c) => c.attendee_id));
  const restAttendees = attendees
    .filter((a) => !suggestionIds.has(a.id))
    .slice() // don't mutate caller list
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div
      className="grid grid-cols-[auto_1fr_auto] gap-3 items-center px-3 py-2.5 text-sm hover:bg-muted/30"
      data-testid={`rsvp-row-${idx}`}
    >
      <div className="flex items-center gap-2 shrink-0">
        {statusBadge(row)}
        <span className="text-[10px] font-mono text-muted-foreground w-[34px] text-right">
          {row.best_score}%
        </span>
      </div>

      <div className="min-w-0">
        <div className="font-medium truncate flex items-center gap-2" data-testid={`rsvp-input-${idx}`}>
          {row.input_name}
          {row.manually_set && (
            <span className="text-[10px] text-primary font-normal">(manual)</span>
          )}
        </div>
        {row.status === 'R' && row.matched_name && (
          <div className={`text-xs truncate ${row.is_duplicate ? 'text-amber-700' : 'text-muted-foreground'}`}>
            {row.is_duplicate ? '↩ already counted — matches ' : '→ matched '}
            <span className="font-medium">{row.matched_name}</span>
          </div>
        )}
        {row.status === 'NS' && (
          <div className="text-xs text-yellow-800">
            Best guess: <span className="font-medium">{row.candidates?.[0]?.name}</span> ({row.candidates?.[0]?.score}%) — confirm or pick another
          </div>
        )}
        {row.status === 'NR' && (
          <div className="text-xs text-rose-700">
            No confident match — pick from the full list or add as a walk-in
          </div>
        )}
      </div>

      <div className="shrink-0 flex items-center gap-1.5">
        <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
          <PopoverTrigger asChild>
            <Button
              size="sm"
              variant="outline"
              className="h-7 gap-1.5 text-xs"
              data-testid={`rsvp-change-match-${idx}`}
            >
              <Pencil className="w-3 h-3" />
              {row.matched_attendee_id ? 'Change' : 'Match'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="p-0 w-[320px]" align="end">
            <Command
              filter={(value, search) => {
                if (!search) return 1;
                return value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0;
              }}
            >
              <CommandInput placeholder="Search all registered members…" data-testid={`rsvp-picker-search-${idx}`} />
              <CommandList className="max-h-[280px]">
                <CommandEmpty>No one found</CommandEmpty>
                {(row.candidates || []).length > 0 && (
                  <>
                    <CommandGroup heading="Suggested matches">
                      {row.candidates.map((c) => (
                        <CommandItem
                          key={`sugg-${c.attendee_id}`}
                          value={`${c.name} ${c.attendee_id}`}
                          onSelect={() => {
                            const att = attendeesById[c.attendee_id] || { id: c.attendee_id, name: c.name };
                            onPickMatch(idx, att);
                            setPickerOpen(false);
                          }}
                          data-testid={`rsvp-suggested-${c.attendee_id}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="truncate">{c.name}</div>
                            <div className="text-[10px] text-muted-foreground">{c.score}% match</div>
                          </div>
                          {row.matched_attendee_id === c.attendee_id && <Check className="w-3.5 h-3.5 text-primary" />}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                    <CommandSeparator />
                  </>
                )}
                {restAttendees.length > 0 && (
                  <CommandGroup heading={`All registered (${restAttendees.length})`}>
                    {restAttendees.map((a) => (
                      <CommandItem
                        key={`all-${a.id}`}
                        value={`${a.name} ${a.email || ''}`}
                        onSelect={() => {
                          onPickMatch(idx, a);
                          setPickerOpen(false);
                        }}
                        data-testid={`rsvp-attendee-${a.id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="truncate">{a.name}</div>
                          {a.email && !a.email.startsWith('walkin+') && (
                            <div className="text-[10px] text-muted-foreground truncate">{a.email}</div>
                          )}
                        </div>
                        {row.matched_attendee_id === a.id && <Check className="w-3.5 h-3.5 text-primary" />}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
                <CommandSeparator />
                <CommandGroup heading="Other actions">
                  {row.status !== 'NR' && (
                    <CommandItem
                      value="mark-nr"
                      onSelect={() => { onForceNR(idx); setPickerOpen(false); }}
                      data-testid={`rsvp-force-nr-${idx}`}
                    >
                      <span className="text-rose-700">Mark as NOT registered</span>
                    </CommandItem>
                  )}
                  <CommandItem
                    value="remove"
                    onSelect={() => { onRemove(idx); setPickerOpen(false); }}
                    data-testid={`rsvp-remove-${idx}`}
                  >
                    <span className="text-muted-foreground">Remove this row</span>
                  </CommandItem>
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        {row.status === 'NR' && (
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5 h-7 text-xs"
            onClick={() => onWalkIn(idx, row)}
            data-testid={`rsvp-add-walkin-${idx}`}
          >
            <UserPlus className="w-3 h-3" />
            Walk-in
          </Button>
        )}
      </div>
    </div>
  );
}
